/**
 * Depot seeding for the preset-action e2e probe, split out so it is unit-testable.
 *
 * WHY SEPARATE: the probe itself imports presetActionReceipt, whose module graph
 * reaches a Vite `?worker` import that jest cannot load. Keeping the seed factory
 * in its own module — importing only depot types and models — lets the seed
 * contract be tested directly, the same way calibration-probe.ts factors
 * createCalibrationSetStatus out of its install branch.
 *
 * WHY IT EXISTS AT ALL: the canonical event map marks org_id required for both
 * position-preset events, and presetActionReceipt resolves it ONLY from
 * depot.roomRecords[studio_id].organizationId, reached via the room that actually
 * contains the device. A synthetic device id resolves no room, so the receipt emits
 * without org_id and the canonical matcher rejects it.
 *
 * ISOLATION IS PART OF THE CONTRACT. The Electron e2e fixture is shared and does
 * not reset the depot between specs, so this seam:
 *   - writes ONLY a device key that does not already exist, into the displayed room,
 *     and REFUSES an occupied id rather than replacing an operator's device;
 *   - deletes exactly that key on release — success or failure — retaining only
 *     plain strings, never a value read out of the producer's draft;
 *   - NEVER fabricates an organization on a real room record. org_id must come from
 *     an organization the session already has; with none, the seed refuses with
 *     `no-organization` so the receipt fails clearly instead of mutating shared
 *     account state or emitting an unmatchable event.
 * It never resets the depot wholesale and performs no API/backend write.
 *
 * Per rule: stay-in-scope + investigate-before-adding (ai.knowledge/global).
 * Per rule: reference-rules-in-comments (ai.knowledge/global).
 */
import type { Producer } from '#~/depot/index.js';
import { castId } from '@studio/models';
import { Protocol } from '@studio/models/control';
import type { Agent } from '@studio/models/agent';
import type { Device } from '@studio/models/device';
import { DeviceDefaults } from '@studio/models/device';
import type { Room } from '@studio/models/room';

const ACTIVE_ROOM_PATH = /^\/room\/([^/]+)/;

const resolveActivePathname = (): string =>
  typeof window === 'undefined' ? '' : window.location.pathname;

const resolveActiveRoomId = (pathname: string): string | undefined =>
  ACTIVE_ROOM_PATH.exec(pathname)?.[1];

/** Why a seed refused. Each one is a clear e2e failure, never a silent pass. */
export type PresetProbeSeedRefusal =
  | 'no-room-id'
  | 'room-not-in-depot'
  | 'no-organization'
  | 'device-occupied';

/** What the probe did, so the receipt spec asserts seeding instead of assuming it. */
export interface PresetProbeSeedReceipt {
  seeded: boolean;
  reason?: PresetProbeSeedRefusal;
  roomId?: string;
  organizationId?: string;
}

/**
 * What the seam created and must remove. Only plain strings are retained: anything
 * read out of the producer's draft is an Immer proxy that is REVOKED the moment
 * produce() returns, so keeping one and writing it back later throws
 * "Cannot perform 'get' on a proxy that has been revoked" and leaves the fixture
 * mutated. That is why this seam never snapshots a device — see the occupied-id
 * policy in seed().
 */
interface OwnedSeed {
  roomId: Room['id'];
  deviceId: Device['id'];
}

/** The seed outcome plus proof the seam gave the device back. */
export interface PresetProbeRunReceipt extends PresetProbeSeedReceipt {
  released: boolean;
  /** Device ids still held after the run — must be empty. */
  leaked: string[];
}

export interface PresetProbeSeam {
  seed(deviceId: string): PresetProbeSeedReceipt;
  /** Undo exactly what this seam wrote. Safe to call when nothing is owned. */
  release(deviceId: string): boolean;
  /** Device ids this seam is currently holding — a leak check for the spec. */
  owned(): string[];
}

/**
 * Build the seam against an injected producer accessor, so the contract is
 * unit-testable with a plain draft and the install side effect stays gated.
 */
export const createPresetProbeSeam = (getProduce: () => Producer): PresetProbeSeam => {
  const owned = new Map<string, OwnedSeed>();

  const seed = (deviceId: string): PresetProbeSeedReceipt => {
    const roomId = resolveActiveRoomId(resolveActivePathname());
    if (!roomId) {
      return { seeded: false, reason: 'no-room-id' };
    }

    let outcome: PresetProbeSeedReceipt = { seeded: false, reason: 'room-not-in-depot' };
    // Check-then-act must stay inside one produce(), as calibration-probe.ts does.
    getProduce()((draft) => {
      const typedRoomId = castId<Room['id']>(roomId);
      const room = draft.rooms[typedRoomId];
      if (!room) {
        return;
      }

      // org_id is a REQUIRED canonical property and belongs to the account, not to
      // this probe. Refuse rather than invent one on a real room record.
      const organizationId = draft.roomRecords[typedRoomId]?.organizationId;
      if (!organizationId) {
        outcome = { seeded: false, reason: 'no-organization', roomId };
        return;
      }

      const id = castId<Device['id']>(deviceId);

      // Occupied-id policy: refuse rather than replace. The receipt generates a
      // unique id per run, so a collision is a real anomaly, not a case to paper
      // over — and "restore what was there" is not implementable safely here,
      // because the previous value is an Immer draft that is revoked as soon as
      // this produce() returns. Refusing keeps the operator's device untouched and
      // leaves nothing to track.
      if (room.devices[id] !== undefined) {
        outcome = { seeded: false, reason: 'device-occupied', roomId };
        return;
      }

      owned.set(deviceId, { roomId: typedRoomId, deviceId: id });

      room.devices[id] = {
        ...DeviceDefaults,
        id,
        agentId: castId<Agent['id']>('e2e-preset-fixture-agent'),
        control: {
          config: {
            protocol: Protocol.HTTP,
            host: 'iris-e2e-fixture.local',
            port: 0
          }
        }
      } as Device;

      outcome = { seeded: true, roomId, organizationId: organizationId };
    });
    return outcome;
  };

  const release = (deviceId: string): boolean => {
    const entry = owned.get(deviceId);
    if (!entry) {
      return false;
    }
    getProduce()((draft) => {
      const room = draft.rooms[entry.roomId];
      if (!room) {
        return;
      }
      // Only ever the key this seam created: an occupied id was refused, never
      // seeded, so there is no previous value to put back.
      delete room.devices[entry.deviceId];
    });
    // Ownership is dropped only AFTER the write lands. If the producer throws,
    // the seam still knows it holds the device rather than reporting a clean
    // owned() while the fixture keeps it.
    owned.delete(deviceId);
    return true;
  };

  return { seed, release, owned: () => [...owned.keys()] };
};

/**
 * Seed, run the caller's emit, and release — in one guaranteed try/finally.
 *
 * Orchestration lives here rather than in the probe so the release-on-throw
 * guarantee is unit-testable: the probe's own module graph reaches a Vite
 * `?worker` import jest cannot load. The emit is injected, so this file still
 * imports nothing heavy.
 *
 * A refused seed releases nothing because it wrote nothing. A THROWING emit still
 * releases, then rethrows: the Electron e2e fixture is shared and never resets the
 * depot, so a failing assertion must not leave the seeded device visible to every
 * later spec.
 */
export const runWithSeed = (
  seam: PresetProbeSeam,
  deviceId: string,
  emit: () => void
): PresetProbeRunReceipt => {
  const receipt = seam.seed(deviceId);
  if (!receipt.seeded) {
    return { ...receipt, released: false, leaked: seam.owned() };
  }
  let released = false;
  try {
    emit();
  } finally {
    released = seam.release(deviceId);
  }
  return { ...receipt, released, leaked: seam.owned() };
};
